import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { IWindow } from "@/store/slices/windows.slice";
import { explorerItemsList } from "@/data/explorer.items.list";
import { SearchFileEntry } from "@/models/explorer.item";
import { getIconFC } from "@/components/icons";
import { useAppSelector } from "@/hooks";
import { selectUser } from "@/store/slices/auth.slice";
import ImageViewer from "@/components/image.viewer";
import { ExplorerItem } from "@/models/explorer.item";
import { downloadFile_pdf } from "../pdf/download.trigger";
import { downloadFile_mp4 } from "../mp4/download.trigger";
import { setSearchQueryLog, setSearchResultClickLog } from "@/firebase/logs";
import * as St from "./styles";

interface IntranetSearchProps {
  activeWindow: IWindow;
}

const SEARCH_HISTORY_KEY = "intranet_search_history";
const MAX_HISTORY_ITEMS = 10;

const IntranetSearch: React.FC<IntranetSearchProps> = ({ activeWindow }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState<SearchFileEntry | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const user = useAppSelector(selectUser);

  const explorerItem = useMemo(() => {
    return explorerItemsList.getItemById(activeWindow.currentExplorer)!;
  }, [activeWindow.currentExplorer]);

  const fileKeywordIndex = useMemo(() => {
    return explorerItem.file_keyword_index || [];
  }, [explorerItem.file_keyword_index]);

  // Get pre-entered searches
  const preEnteredSearches = useMemo(() => {
    return fileKeywordIndex.filter((file) => file.pre_entered);
  }, [fileKeywordIndex]);

  // Load search history from local storage on mount
  useEffect(() => {
    if (user?.id) {
      const storageKey = `${SEARCH_HISTORY_KEY}_${user.id}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const history = JSON.parse(stored);
          setSearchHistory(Array.isArray(history) ? history : []);
        } catch (e) {
          console.error("Failed to parse search history", e);
        }
      }
    }
  }, [user?.id]);

  // Save search to history
  const saveToHistory = useCallback((query: string) => {
    if (!user?.id || !query.trim() || query.trim().length < 3) return;
    
    const normalizedQuery = query.trim();
    
    setSearchHistory((prev) => {
      // Remove duplicates and add to front
      const updated = [normalizedQuery, ...prev.filter(q => q !== normalizedQuery)].slice(0, MAX_HISTORY_ITEMS);
      
      // Save to local storage
      const storageKey = `${SEARCH_HISTORY_KEY}_${user.id}`;
      localStorage.setItem(storageKey, JSON.stringify(updated));
      
      return updated;
    });
  }, [user?.id]);

  // Normalize search string (lowercase, trim whitespace)
  const normalizeString = useCallback((str: string): string => {
    return str.toLowerCase().trim().replace(/\s+/g, " ");
  }, []);

  // Search algorithm: case-insensitive, partial keyword matching
  const searchResults = useMemo(() => {
    const normalized = normalizeString(searchQuery);
    
    // Only show results if 3+ characters entered
    if (normalized.length < 3) {
      return [];
    }

    return fileKeywordIndex.filter((file) => {
      // Check if any keyword partially matches the normalized input
      return file.keywords.some((keyword) => {
        const normalizedKeyword = normalizeString(keyword);
        return normalizedKeyword.includes(normalized) || normalized.includes(normalizedKeyword);
      });
    });
  }, [searchQuery, fileKeywordIndex, normalizeString]);

  // Combine pre-entered and history for dropdown
  const dropdownItems = useMemo(() => {
    const items: Array<{ text: string; isPreEntered: boolean }> = [];
    
    // Add pre-entered searches first
    preEnteredSearches.forEach(file => {
      if (file.pre_entered_text) {
        items.push({ text: file.pre_entered_text, isPreEntered: true });
      }
    });
    
    // Add user search history (avoid duplicates)
    searchHistory.forEach(query => {
      if (!items.some(item => item.text.toLowerCase() === query.toLowerCase())) {
        items.push({ text: query, isPreEntered: false });
      }
    });
    
    return items;
  }, [preEnteredSearches, searchHistory]);

  // Handle clicking on dropdown item
  const handleDropdownItemClick = useCallback((searchText: string) => {
    setSearchQuery(searchText);
    setShowDropdown(false);
    // Save to history when clicking on a dropdown item
    saveToHistory(searchText);
    // Log the search query to Firestore
    setSearchQueryLog(searchText);
  }, [saveToHistory]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    setShowDropdown(true);
  }, []);

  // Handle pressing Enter to search
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.trim().length >= 3) {
      saveToHistory(searchQuery);
      setShowDropdown(false);
      // Log the search query to Firestore
      setSearchQueryLog(searchQuery);
    }
  }, [searchQuery, saveToHistory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Handle clicking on a search result
  const handleResultClick = useCallback((file: SearchFileEntry) => {
    setSelectedFile(file);
    // Log the result click to Firestore
    setSearchResultClickLog(searchQuery, file.title);
  }, [searchQuery]);

  // Handle going back to search
  const handleBackToSearch = useCallback(() => {
    setSelectedFile(null);
  }, []);

  // Auto-download PDFs and MP4s when selected
  useEffect(() => {
    if (selectedFile) {
      if (selectedFile.type === "pdf") {
        downloadFile_pdf(selectedFile.file_url, selectedFile.title);
        setSelectedFile(null);
      } else if (selectedFile.type === "mp4") {
        downloadFile_mp4(selectedFile.file_url, selectedFile.title);
        setSelectedFile(null);
      }
    }
  }, [selectedFile]);

  // If a file is selected and it's an image, show the image viewer
  if (selectedFile && selectedFile.type === "image") {
    const tempExplorerItem = new ExplorerItem({
      title: selectedFile.title,
      icon: selectedFile.icon,
      type: selectedFile.type,
      imageUrl: selectedFile.file_url,
      selected_difficulty: ["easy", "medium", "hard"],
      episode: explorerItem.episode,
    });

    return (
      <St.SearchWrapper>
        <St.SearchMenu activeWindow={activeWindow} />
        <St.SearchContentWrapper>
          <St.MenuButton onClick={handleBackToSearch}>
            ← Zurück zur Suche
          </St.MenuButton>
          <ImageViewer explorerItem={tempExplorerItem} />
        </St.SearchContentWrapper>
        <St.SearchInfoBox>
          <span>{selectedFile.title}</span>
          <span></span>
        </St.SearchInfoBox>
      </St.SearchWrapper>
    );
  }

  // Main search interface
  return (
    <St.SearchWrapper>
      {/* Menu */}
      <St.SearchMenu activeWindow={activeWindow} />

      {/* Content */}
      <St.SearchContentWrapper>
        {/* Search Input */}
        <St.SearchInputWrapper>
          <St.SearchLabel>Intranet Suche:</St.SearchLabel>
          <St.SearchInputContainer>
            <St.SearchInput
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder="Suchbegriff eingeben..."
            />
            
            {/* Dropdown with search history */}
            {showDropdown && dropdownItems.length > 0 && (
              <St.SearchDropdown ref={dropdownRef}>
                {dropdownItems.map((item, index) => (
                  <St.DropdownItem
                    key={index}
                    onClick={() => handleDropdownItemClick(item.text)}
                  >
                    {item.text}
                  </St.DropdownItem>
                ))}
              </St.SearchDropdown>
            )}
          </St.SearchInputContainer>
        </St.SearchInputWrapper>

        {/* Search Results */}
        {searchQuery.length >= 3 && (
          <St.SearchResultsWrapper>
            <St.SearchResultsLabel>
              Suchergebnisse ({searchResults.length}):
            </St.SearchResultsLabel>
            {searchResults.length > 0 ? (
              <St.SearchResultsList>
                {searchResults.map((file, index) => {
                  const Icon = getIconFC(file.icon);
                  return (
                    <St.SearchResultItem
                      key={index}
                      onClick={() => handleResultClick(file)}
                    >
                      <St.ResultIcon>
                        <Icon css={{ size: "2.4rem" }} />
                      </St.ResultIcon>
                      <span>{file.title}</span>
                    </St.SearchResultItem>
                  );
                })}
              </St.SearchResultsList>
            ) : (
              <St.NoResultsMessage>
                Keine Ergebnisse gefunden. Versuchen Sie andere Suchbegriffe.
              </St.NoResultsMessage>
            )}
          </St.SearchResultsWrapper>
        )}

        {/* Hint when search query is too short */}
        {searchQuery.length > 0 && searchQuery.length < 3 && (
          <St.NoResultsMessage>
            Bitte mindestens 3 Zeichen eingeben...
          </St.NoResultsMessage>
        )}
      </St.SearchContentWrapper>

      {/* Info Box */}
      <St.SearchInfoBox>
        <span>Intranet Archiv</span>
        <span></span>
      </St.SearchInfoBox>
    </St.SearchWrapper>
  );
};

export default IntranetSearch;

